import React, { useEffect, useState } from 'react';
import { CheckCircle2, Info, AlertTriangle, X, XCircle } from 'lucide-react';
import { cn } from '../../utils/cn';

interface ToastProps {
  type: 'success' | 'error' | 'info' | 'warning';
  message: string;
  onClose: () => void;
}

const iconMap = {
  success: CheckCircle2,
  error: XCircle,
  info: Info,
  warning: AlertTriangle,
};

const baseStyles =
  'pointer-events-auto w-full max-w-sm rounded-lg shadow-lg ring-1 ring-black ring-opacity-5 bg-white p-4 transition transform';

const typeStyles: Record<ToastProps['type'], string> = {
  success: 'border-l-4 border-emerald-500',
  error: 'border-l-4 border-red-500',
  info: 'border-l-4 border-blue-500',
  warning: 'border-l-4 border-amber-500',
};

const iconClasses: Record<ToastProps['type'], string> = {
  success: 'text-emerald-500',
  error: 'text-red-500',
  info: 'text-blue-500',
  warning: 'text-amber-500',
};

const Toast: React.FC<ToastProps> = ({ type, message, onClose }) => {
  const [visible, setVisible] = useState(false);
  const Icon = iconMap[type];

  useEffect(() => {
    // Small delay for enter animation
    const t = window.setTimeout(() => setVisible(true), 10);
    return () => window.clearTimeout(t);
  }, []);

  const handleClose = () => {
    setVisible(false);
    // Give exit animation time to finish
    window.setTimeout(onClose, 150);
  };

  return (
    <div
      className={cn(
        baseStyles,
        typeStyles[type],
        visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'
      )}
      role="status"
      aria-live="polite"
    >
      <div className="flex">
        <div className="flex-shrink-0">
          <Icon className={cn('h-5 w-5', iconClasses[type])} aria-hidden="true" />
        </div>
        <div className="ml-3 flex-1 text-sm text-gray-900">{message}</div>
        <div className="ml-4">
          <button
            type="button"
            className="inline-flex rounded-md bg-white text-gray-400 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
            onClick={handleClose}
            aria-label="Dismiss notification"
          >
            <X className="h-4 w-4" aria-hidden="true" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default Toast;
